import { useState } from "react";
import { ApplyButton, SectionHeader } from "./shared";

const MIN_AMOUNT = 5000;
const MAX_AMOUNT = 50000;
const MIN_TENURE = 61;
const MAX_TENURE = 90;
const PROCESSING_FEE_RATE = 0.05;

const formatINR = (n: number) => `₹${n.toLocaleString("en-IN")}`;
const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

const label = "font-extrabold text-[#5b168c] text-[14px] tracking-[0.5px] uppercase";
const hint = "text-[#6a7282] text-[13px] leading-[18px]";

export default function HomeCalculator() {
  const [amount, setAmount] = useState(25000);
  const [amountInput, setAmountInput] = useState(formatINR(25000));
  const [tenure, setTenure] = useState(75);

  const fillPct = ((amount - MIN_AMOUNT) / (MAX_AMOUNT - MIN_AMOUNT)) * 100;
  const processingFee = Math.round(amount * PROCESSING_FEE_RATE);

  const updateAmount = (value: number) => {
    const next = clamp(value, MIN_AMOUNT, MAX_AMOUNT);
    setAmount(next);
    setAmountInput(formatINR(next));
  };

  const summaryRows = [
    { label: "Estimated Interest", value: "Pending verification" },
    { label: "Processing Fee", value: formatINR(processingFee) },
    { label: "Verified APR", value: "Pending verification" },
    { label: "Taxes", value: "Pending confirmation" },
  ];

  return (
    <section className="bg-white px-4 sm:px-6 lg:px-[80px] py-[60px]">
      <div className="max-w-[1280px] mx-auto flex flex-col lg:flex-row gap-8">
        <div className="w-full lg:w-[400px] shrink-0 flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <SectionHeader align="left" badge="Loan Calculator" title="Calculate Your Loan Repayment" />
            <p className="text-[#6a7282] text-[16px] leading-[28px]">
              Choose your loan amount and repayment tenure to understand your estimated borrowing cost.
            </p>
          </div>

          <div className="flex flex-col gap-6 bg-[#f8fafc] border border-[#f3f4f6] rounded-[24px] p-6 sm:p-8 drop-shadow-[0px_12px_16px_rgba(6,78,59,0.07)]">
            <div className="flex flex-col gap-2">
              <label htmlFor="home-calc-amount" className={label}>Loan Amount</label>
              <div className="flex flex-col gap-3">
                <div className="relative h-5 flex items-center">
                  <div className="relative w-full h-2 rounded-full bg-[#f3f4f6]">
                    <div className="h-2 rounded-full bg-[#5c168a]" style={{ width: `${fillPct}%` }} />
                    <div
                      className="absolute top-[-6px] size-5 -ml-2.5 rounded-full bg-[#5c168a] shadow-[0px_4px_12px_0px_rgba(92,22,138,0.2)]"
                      style={{ left: `${fillPct}%` }}
                    />
                  </div>
                  <input
                    type="range"
                    min={MIN_AMOUNT}
                    max={MAX_AMOUNT}
                    step={500}
                    value={amount}
                    onChange={(e) => updateAmount(Number(e.target.value))}
                    aria-label="Loan amount"
                    className="absolute inset-0 w-full opacity-0 cursor-pointer"
                  />
                </div>
                <div className="flex justify-between text-[#6a7282] text-[13px]">
                  <span>{formatINR(MIN_AMOUNT)}</span>
                  <span>{formatINR(MAX_AMOUNT)}</span>
                </div>
              </div>
              <input
                id="home-calc-amount"
                inputMode="numeric"
                value={amountInput}
                onChange={(e) => setAmountInput(e.target.value)}
                onBlur={() => updateAmount(Number(amountInput.replace(/\D/g, "")) || MIN_AMOUNT)}
                onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                className="w-full bg-white border border-[#5b1a8b] rounded-[12px] px-4 py-3 font-bold text-[#1a1a4b] text-[22px] outline-none"
              />
              <p className={hint}>Select the amount you want to explore. Final amount subject to eligibility and assessment.</p>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="home-calc-tenure" className={label}>Repayment Tenure</label>
              <div className="relative flex items-center justify-between bg-white border border-[#5b1a8b] rounded-[12px] px-4 py-3 font-bold">
                <span className="text-[#1a1a4b] text-[22px]">{tenure} days</span>
                <span className="text-[#5b168c] text-[13px]">Select tenure</span>
                <select
                  id="home-calc-tenure"
                  value={tenure}
                  onChange={(e) => setTenure(Number(e.target.value))}
                  className="absolute inset-0 w-full opacity-0 cursor-pointer"
                >
                  {Array.from({ length: MAX_TENURE - MIN_TENURE + 1 }, (_, i) => MIN_TENURE + i).map((d) => (
                    <option key={d} value={d}>{d} days</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-between text-[#6a7282] text-[13px]">
                <span>{MIN_TENURE} days</span>
                <span>{MAX_TENURE} days</span>
              </div>
              <p className={hint}>Choose the repayment period you want to review. Final tenure subject to eligibility and assessment.</p>
            </div>

            <div className="flex flex-col gap-2">
              <p className={label}>Interest Rate &amp; Processing Fee</p>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1 bg-white border border-[#f3f4f6] rounded-[16px] p-4">
                  <p className="font-extrabold text-[#5b168c] text-[13px] tracking-[0.5px] uppercase">Interest Rate</p>
                  <p className="font-bold text-[#1a1a4b] text-[18px]">3% per 30-day period</p>
                  <p className="text-[#6a7282] text-[12px] leading-[16px]">Published rate only; APR and final interest pending confirmation.</p>
                </div>
                <div className="flex flex-col gap-1 bg-white border border-[#f3f4f6] rounded-[16px] p-4">
                  <p className="font-extrabold text-[#5b168c] text-[13px] tracking-[0.5px] uppercase">Processing Fee</p>
                  <p className="font-bold text-[#1a1a4b] text-[18px]">5% of loan amount</p>
                  <p className="text-[#6a7282] text-[12px] leading-[16px]">Base fee shown; collection timing and taxes pending confirmation.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-0 flex">
          <div className="w-full flex flex-col gap-6 bg-[#f8fafc] border border-[#f3f4f6] rounded-[24px] p-6 sm:p-8 drop-shadow-[0px_12px_16px_rgba(6,78,59,0.07)]">
            <div className="flex flex-col gap-2">
              <p className="font-bold text-[#1a1a4b] text-[26px] sm:text-[32px]">Repayment Summary</p>
              <p className="text-[#6a7282] text-[14px] leading-[22px]">
                Based on {formatINR(amount)} selected amount and {tenure} days selected tenure. Final loan terms are subject to lender approval and applicable loan agreement.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-2 bg-white border border-[#5c168a] rounded-[20px] p-5">
                <p className={label}>Total Repayment</p>
                <p className="font-bold text-[#1a1a4b] text-[24px] sm:text-[28px]">Pending verification</p>
                <p className={hint}>Includes applicable interest, fees, and taxes once final loan terms are confirmed.</p>
              </div>
              {summaryRows.map((row) => (
                <div key={row.label} className="flex items-center justify-between gap-3 bg-white border border-[#f3f4f6] rounded-[16px] p-4">
                  <span className={label}>{row.label}</span>
                  <span className="font-bold text-[#1a1a4b] text-[15px] sm:text-[18px] text-right">{row.value}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-3">
              <ApplyButton label="Apply for a Loan" location="home_calculator" className="w-full px-10 py-4" />
              <p className="text-[#6a7282] text-[12px] leading-[18px] text-center">
                Displayed figures are estimates. Final loan terms are subject to lender approval and applicable loan agreement.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
